import React, { useContext, useEffect, useMemo } from 'react';
import { shape as shapeProps, arrayOf as arrayOfProps } from 'prop-types';
import {
  params, string, arrayOf, bool, shape, QueryContext, extend
} from '@thd-nucleus/data-sources';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  useBundlePricingDetails,
  getBundleWithPricing
} from '@thd-olt-component-react/product-bundle';
import { Price } from '@thd-olt-component-react/price';
import { Promotion } from '@thd-olt-component-react/promotion';
import { transformBundlePromotion } from '../utils/promotion-utils';
import { NONE_VALUE } from '../constants/laundry';
import { LaundryAddToCart } from './laundry/LaundryAddToCart';

/**
 * LaundryPrice component displays the pricing details for a laundry bundle,
 * including selected products and accessories, and provides an option to add the bundle to the cart.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.bundle - The main bundle product object.
 * @param {Array<Object>} props.products - Array of selected product objects.
 * @param {Object} props.accessories - Accessories selection object, containing selectedVariant and products.
 * @returns {JSX.Element} The rendered LaundryPrice component.
 */
export const LaundryPrice = ({ bundle, products, accessories }) => {
  const { defaultVariables } = useContext(QueryContext) || {};
  const isCustomerIdentified = defaultVariables?.current?.isBrandPricingPolicyCompliant || false;
  const isAccessoriesSelected = (accessories?.selectedVariant !== NONE_VALUE && accessories?.products?.length > 0);
  const priceTitle = isAccessoriesSelected ? 'Price for 2 Appliances + Accessories' : 'Price for 2 Appliances';

  const selectedAccessories = useMemo(() =>
    (accessories?.selectedVariant === NONE_VALUE ? [] : accessories?.products || [])
  , [accessories]);

  useEffect(() => {
    if (products.length > 0) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-bundle.update-price-adjustment', {
        accessoriesData: selectedAccessories || [],
        bundleProducts: products
      });
    }
  }, [products, selectedAccessories]);

  const { pricing, adjustments } = useBundlePricingDetails({
    product: bundle,
    products,
    selectedAccessories,
    loading: false,
    isCustomerIdentified
  }) || {};

  const bundlePromotionData = useMemo(() => {
    return transformBundlePromotion(products, adjustments);
  }, [adjustments, products]);

  const showBundlePromo = !!bundlePromotionData?.product?.pricing?.conditionalPromotions?.length;

  if (!pricing) {
    return null;
  }

  return (
    <div>
      <div className="sui-mb-2">
        <Typography height="tight" variant="body-base">
          {priceTitle}
        </Typography>
      </div>
      <Price
        itemId={bundle?.itemId}
        product={getBundleWithPricing({ bundle, pricing })}
      />
      {showBundlePromo && (
        <div className="sui-my-4" data-testid="laundry-promo-tag">
          <Promotion itemId={bundle?.itemId} pageType="BIP" promotionData={bundlePromotionData} />
        </div>
      )}
      <LaundryAddToCart bundle={bundle} products={products} accessories={selectedAccessories} />
    </div>
  );
};

LaundryPrice.propTypes = {
  bundle: shapeProps({}).isRequired,
  products: arrayOfProps(shapeProps({})).isRequired,
  accessories: shapeProps({})
};

LaundryPrice.defaultProps = {
  accessories: null
};

LaundryPrice.dataModel = extend({
  products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
    info: shape({
      quantityLimit: string(),
      isInStoreReturnMessageEligible: bool(),
      returnable: string()
    }),
    fulfillment: {
      anchorStoreStatusType: string(),
      backordered: bool(),
      backorderedShipDate: string(),
      bossExcludedShipStates: string(),
      bodfsAssemblyEligible: bool(),
      seasonStatusEligible: bool(),
      excludedShipStates: string(),
      fulfillmentOptions: shape({
        fulfillable: bool(),
        services: shape({
          type: string(),
          isDefault: bool(),
          deliveryCharge: string(),
          deliveryDates: shape({
            endDate: string(),
            startDate: string()
          }),
          deliveryTimeline: string(),
          freeDeliveryThreshold: string(),
          totalCharge: string(),
          locations: shape({
            type: string(),
            isAnchor: bool(),
            locationId: string(),
            storeName: string(),
            storePhone: string(),
            state: string(),
            distance: string(),
            inventory: shape({
              quantity: string(),
              isInStock: bool(),
              isOutOfStock: bool(),
              isLimitedQuantity: bool(),
              isUnavailable: bool(),
              maxAllowedBopisQty: string(),
              minAllowedBopisQty: string()
            })
          })
        }),
        type: string()
      })
    }
  })
},
Price,
Promotion);